/**
 * Every visible string in the complexity game, in one place for translation.
 * Question and option labels come from the data file (complexity-game.json);
 * `inputLabels` fills the inputs that have no label there. House rule: no em
 * dashes or en dashes anywhere in copy; ranges read "4 to 6".
 */
const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
const numWord = (n: number): string => NUMBER_WORDS[n] ?? String(n);

/** Accountant share of a working month, as a phrase. */
function accountantShare(share: number): string {
  if (share >= 1) return 'full-time';
  if (share === 0.75) return 'three-quarter time';
  if (share === 0.5) return 'half-time';
  return `${Math.round(share * 100)} percent of the time`;
}

/** Finance manager share of a five-day week, as a phrase. */
function managerShare(share: number): string {
  const days = share * 5;
  if (days === 1) return 'one day a week';
  if (days === 1.5) return 'a day and a half a week';
  if (Number.isInteger(days)) return `${numWord(days)} days a week`;
  return `${Math.round(share * 100)} percent of the time`;
}

export const STR = {
  kicker: 'PS Global scoping game',
  title: 'How big is your NetSuite project, really?',
  subtitle:
    'Start from the simplest project, one Hong Kong company on financials, and add what is true for you. The meter climbs and cost lines switch on as you go. Indicative scope, not a quote.',
  logoAlt: 'PS Global Consulting',
  provisional: 'Provisional scoring, PS Global to calibrate',

  stepOf: (n: number, total: number): string => `Step ${n} of ${total}`,
  stepTitles: {
    readiness: 'Readiness',
    structure: 'Structure',
    scope: 'Day-one scope',
    starting_point: 'Starting point',
    history: 'History',
    integrations: 'Integrations',
    tailoring: 'Tailoring',
    people: 'People',
    deadline: 'Deadline',
  } as Record<string, string>,
  inputLabels: {
    entities: 'How many companies go on NetSuite?',
    scope: 'Pick everything that has to work on day one',
    source: 'Your current system',
    history: 'How much comes across',
    customisation: 'How much is built specially for you',
    full_users: 'How many people post transactions',
    training_languages: 'Training languages',
    deadline: 'Go-live target',
  } as Record<string, string>,
  multiHint: 'Choose all that apply',
  prefilled: 'Filled in from the edition checker above. Change it if it is wrong.',
  rating: {
    low: '1 Not yet',
    high: '5 Fully',
    value: (label: string, n: number): string => `${label}: ${n} of 5`,
  },
  publishedBy: 'PS Global',
  hkTag: 'Hong Kong specific',

  back: 'Back',
  next: 'Next',
  seeResult: 'See my result',
  startAgain: 'Start again',
  toggleOn: 'Yes',
  toggleOff: 'No',

  meter: {
    title: 'Complexity',
    level: (n: number, name: string): string => `Level ${n} of 5: ${name}`,
    points: (p: number): string => `${p} pts`,
    rescue: 'Rescue track',
    rescueHint: 'A diagnostic comes first, so the meter stops here.',
    live: 'Already on NetSuite',
    liveHint: 'Nothing to size here. The next screen points you to the right team.',
  },
  costLines: {
    title: 'Cost lines switched on',
    empty: 'Nothing extra yet. A core financials build on one Hong Kong company.',
    count: (n: number): string => (n === 1 ? '1 cost line switched on' : `${n} cost lines switched on`),
    show: 'Show',
    hide: 'Hide',
  },

  integrations: {
    searchLabel: 'Search integrations',
    searchPlaceholder: 'Search by name, for example HSBC or Shopify',
    showAll: (n: number): string => `Show all ${n}`,
    showShortlist: 'Back to the Hong Kong shortlist',
    shortlistNote: 'Showing the systems Hong Kong companies ask about most.',
    allNote: (n: number): string => `All ${n} integrations PS Global has a published page for.`,
    selected: (n: number): string => (n === 1 ? '1 selected' : `${n} selected`),
    none: 'None selected. Skip this step if NetSuite stands alone.',
    weeks: (a: number, b: number): string => `${a} to ${b} weeks`,
    publishedNote: 'Week ranges are the ones PS Global publishes on each integration page.',
    noMatches: 'No integration matches that search. Bring it to the scoping call.',
    clearSearch: 'Clear search',
    bankFact:
      'Hong Kong banks connect by statement file (MT940, CAMT.053, BAI2 or CSV) over SFTP or host-to-host; the NetSuite Bank Feeds SuiteApp does not list Hong Kong.',
    bankFactSource: 'Oracle NetSuite Help',
    status: {
      native: 'Native',
      prebuilt: 'Prebuilt connector',
      mixed: 'Prebuilt plus custom',
      custom: 'Custom build',
    } as Record<string, string>,
  },

  result: {
    kicker: 'Your indicative scope',
    levelLabel: (n: number): string => `Complexity level ${n} of 5`,
    ofFive: '/ 5',
    driversTitle: 'What drove it',
    driversNone: 'Nothing pushed the score up. This is the baseline project.',
    driverPoints: (p: number): string => `+${p}`,
    timelineTitle: 'Core build timeline',
    timelineWeeks: (a: number, b: number): string => `${a} to ${b} weeks`,
    timelineMonths: (a: number, b: number): string => `${a} to ${b} months`,
    timelineBasis: 'Basis: ',
    integrationsTitle: 'Integration lanes',
    integrationsNote:
      'Each integration runs as its own lane with the range PS Global publishes for it. They are not added to the core build above: PS Global has not said whether integrations run alongside the build or after it.',
    integrationsNone: 'No integrations selected. NetSuite stands alone on day one.',
    integrationLink: 'See the integration page',
    costTitle: 'Cost lines switched on, and what is left out',
    costNone: 'Only the core build. No extra cost lines switched on.',
    phaseTitle: 'Where the fee goes',
    phaseSub: "PS Global's published split, pending confirmation",
    phasePct: (a: number, b: number): string => `${a} to ${b}%`,
    feeTitle: 'Implementation fee band',
    feePlaceholder: 'Implementation fee band for this level: to be confirmed by PS Global',
    feeNote: 'PS Global prices its own implementation work. NetSuite subscription pricing is set by Oracle and is not part of this result.',
    secondOpinion: 'Already have a quote? Get a second opinion on the implementation scope.',
    secondOpinionHref: 'https://hk.psglobalconsulting.com/en-us/contact',
    afterTitle: 'After go-live',
    afterBody:
      'Expect a hypercare window of 4 to 8 weeks after go-live, with at least one month-end close run in parallel before you switch off the old system.',
    afterSource: 'PS Global QuickBooks and Xero migration pages. Window to be confirmed by PS Global.',
    scopeNote: 'Indicative scope from your answers. Not a quote, and not a NetSuite subscription figure.',
  },

  notInEstimate: {
    title: 'Not in this estimate',
    rows: {
      oracle_subscription: {
        title: 'NetSuite subscription',
        body: 'Paid to Oracle under your own contract. This game does not price it.',
      },
      connector_subscriptions: {
        title: 'Connector subscriptions',
        body: 'Monthly fees for connectors and middleware such as Celigo, paid to their vendors.',
      },
      team_time: {
        title: "Your own team's time",
        body: '',
      },
      hardware: {
        title: 'Hardware',
        body: 'Barcode scanners, label printers and other devices for the warehouse or shop floor.',
      },
      support: {
        title: 'Support after go-live',
        body: 'Ongoing support or managed services once the hypercare window ends.',
      },
    } as Record<string, { title: string; body: string }>,
  },

  teamTime: {
    about: (hkd: string): string => `About ${hkd} in salary`,
    assumption: (acc: number, fm: number, months: number): string =>
      `An accountant ${accountantShare(acc)}${fm > 0 ? ` and a finance manager ${managerShare(fm)}` : ''} for ${numWord(months)} months, at the JobsDB median${fm > 0 ? 's' : ''}.`,
    salaryOnly: 'Salary only; MPF and other on-costs are not included.',
    source: (acc: string, fm: string): string => `JobsDB monthly medians: accountant ${acc}, finance manager ${fm}.`,
    accountantLink: 'Accountant salary',
    managerLink: 'Finance manager salary',
    provisional: 'Effort per level is provisional.',
    checked: 'Last checked October 2026.',
  },

  readiness: {
    title: 'Readiness',
    outOf: '/ 100',
    intro: 'Rate each from 1 to 5. It takes ten seconds and changes the advice on your result.',
    skipped: 'You skipped the readiness ratings. Go back one step to rate them.',
    partial: (n: number, total: number): string => `Based on ${n} of ${total} ratings.`,
    tiers: {
      early: 'Early days',
      getting_there: 'Getting there',
      ready: 'Ready to scope',
    } as Record<string, string>,
    next: {
      ready_owner: 'Next step: name one person who owns the project and can make decisions, before you ask partners for quotes.',
      ready_data: 'Next step: clean up customer, supplier and item lists first. Migration effort follows data quality.',
      ready_process: 'Next step: write down how month-end close and order-to-cash run today, so every partner scopes the same thing.',
      ready_calendar: 'Next step: move the go-live date clear of year-end close and audit fieldwork.',
      ready_budget: 'Next step: get a budget range signed off before scoping, so the plan is built to fit it.',
      all_good: 'Next step: you are ready to ask partners for a scoped quote. Take the checklist below with you.',
    } as Record<string, string>,
    basis: 'Your own self-rating, modelled on the readiness checklist GURUS Solutions publishes.',
  },

  branch: {
    live: {
      kicker: 'Already on NetSuite',
      title: 'You need a partner, not a project estimate',
      body: "PS Global's managed services team works with companies already live on NetSuite, from day-to-day admin to new workflows, reports and integrations.",
      cta: 'See managed services',
    },
    rescue: {
      kicker: 'Rescue track',
      title: 'A diagnostic comes first',
      body: 'When a NetSuite implementation is not working, PS Global starts with a 2 to 3 week diagnostic, then a remediation plan. Published rescue projects run 8 to 16 weeks and cost 40 to 60 percent less than re-implementing.',
      source: 'PS Global NetSuite rescue page',
      cta: 'Talk to PS Global about a rescue',
    },
    back: 'Back',
    playAnyway: 'Size a new project instead',
  },

  partnerQuestions: {
    users: 'How many users, of which licence types, and which edition does your quote assume?',
    usersFromChecker: (n: number, band: string): string => `${n} full users (from the edition checker), ${band}`,
    entities: 'Which entities and countries are in scope?',
    history: 'How much history is migrated, and how many hours are allowed for it?',
    fromSystems: (n: string): string => `source systems: ${n.toLowerCase()}`,
    banks: 'Which banks, and which statement and payment file formats?',
    noBanks: 'No bank selected in the game; ask how bank files are handled',
    integrations: 'Which integrations are included, by name?',
    testing: 'How many rounds of user testing are included?',
    training: 'Who gets trained, and in which languages?',
    trainingYours: (users: string, langs: string): string => `Training in ${langs}; users posting transactions: ${users.toLowerCase()}`,
    support: 'How long is support after go-live, and what does it cover?',
    teamTime: "How many hours of your team's time does the plan assume?",
    teamTimeYours: (assumption: string, hkd: string): string => `Assumed here: ${assumption} About ${hkd} in salary.`,
  },

  lead: {
    title: 'Take this with you',
    body: 'We will email you this result as a scoping checklist, with your answers written out so you can hand it to a colleague or bring it to a call.',
    name: 'Name',
    email: 'Work email',
    company: 'Company',
    submit: 'Email me this as a scoping checklist',
    sending: 'Sending',
    success: 'Sent. Check your inbox for the scoping checklist.',
    failure: 'That did not send. Try again, or book a scoping call instead.',
    invalidEmail: 'Enter a work email address.',
    required: 'Name and work email are needed to send the checklist.',
    bookCall: 'Book a scoping call',
    bookCallHref: '/en-us/contact/',
    leadLabel: 'Pricing page scoping checklist',
  },

  checklist: {
    heading: 'PS Global scoping checklist (indicative scope, not a quote)',
    intro: 'Generated from the NetSuite pricing page complexity game.',
    answers: 'YOUR ANSWERS',
    result: 'RESULT',
    level: (n: number, name: string): string => `Complexity level ${n} of 5: ${name} (provisional scoring)`,
    source: 'Source: ',
    topDrivers: 'Top drivers: ',
    timeline: 'Core build timeline band: ',
    integrations: 'Integrations (each runs as its own lane, published range):',
    integrationsNone: 'Integrations: none',
    costLines: 'Cost lines switched on:',
    costNone: 'Cost lines switched on: none beyond the core build',
    fee: 'Implementation fee band: to be confirmed by PS Global',
    none: 'None',
    linesPerMonth: (n: number): string => `Transaction lines a month (from the edition checker): ${n.toLocaleString('en-US')}`,
    notInEstimate: 'Not in this estimate:',
    readiness: (score: number, tier: string): string => `Readiness: ${score} out of 100, ${tier}`,
    askEachPartner: 'ASK EACH PARTNER',
    yourAnswer: 'Your answer: ',
    askThem: 'not covered by the game; ask them',
  },

  footer:
    'Sources: PS Global integration, migration and rescue pages; PS Global 2025 cost article; GURUS Solutions and Vendr for the upper timeline tiers; Oracle NetSuite Editions Overview; JobsDB salary medians. Scoring is provisional pending calibration by PS Global. No NetSuite subscription figure is computed here.',
  lastChecked: 'Last checked October 2026',
} as const;
